import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { SchoolProfileForm } from '@/components/school/SchoolProfileForm'
import { LoadingScreen } from '@/components/ui/LoadingScreen'

/**
 * Required post-signup business-profile wizard for a NEW school (migration 080) — run
 * standalone, outside AppShell (no nav chrome), the same pattern as
 * InstructorOnboarding.tsx. ProtectedRoute.tsx redirects a school_admin here whenever
 * check_onboarding_status() reports school_profile_required=true; this page's own job is
 * just to get that flag to false and route onward — update_school_profile() (via
 * SchoolProfileForm/useSchoolProfile) is what actually flips it.
 *
 * Outside AppShell, SelectedSchoolContext isn't available, so this page fetches its own
 * school id directly — always exactly one at this point, since a school_admin only ever
 * reaches this route immediately after create_school() creates their first school.
 */
export default function OnboardingWizard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [schoolId, setSchoolId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    supabase
      .from('school_memberships')
      .select('school_id')
      .eq('user_id', user.id)
      .eq('role', 'OWNER')
      .eq('status', 'active')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setSchoolId(data?.school_id ?? null)
        setLoading(false)
      })
  }, [user])

  const handleSaved = (complete: boolean) => {
    if (complete) navigate('/app/school', { replace: true })
  }

  if (loading) return <LoadingScreen />

  return (
    <div style={{ minHeight: '100vh', background: '#FAF6EF', padding: '32px 16px' }}>
      <div style={{ width: '100%', maxWidth: 640, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={56} />
        </div>

        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 18, padding: '28px 24px' }}>
          <div style={{ font: '600 22px Lora, serif', color: '#1B2A4A', textAlign: 'center' }}>
            Complete your school profile
          </div>
          <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 5, marginBottom: 24 }}>
            A few business details before you get started. You can edit all of this later in Settings.
          </div>

          {!schoolId ? (
            <div className='banner-error'>Could not find your school. Please contact support.</div>
          ) : (
            <SchoolProfileForm schoolId={schoolId} variant='wizard' onSaved={handleSaved} />
          )}
        </div>
      </div>
    </div>
  )
}
